import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchReturnById } from '@/services/returns';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { ReturnRecord } from '@/types/models';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import {
  RotateCcw,
  User,
  CheckCircle2,
  XCircle,
  Loader2,
} from 'lucide-react';

interface ReturnDetailProps {
  returnId: string;
  onClose: () => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-PK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '—';
  return `Rs. ${val.toLocaleString('en-PK')}`;
}

export const ReturnDetail: React.FC<ReturnDetailProps> = ({ returnId, onClose }) => {
  const { data: returnRecord, isLoading, error, refetch } = useQuery<ReturnRecord>({
    queryKey: queryKeys.returns.detail(returnId),
    queryFn: () => fetchReturnById(returnId),
  });

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      )}

      {error && <ErrorAlert message={parseDatabaseError(error)} onRetry={() => refetch()} />}

      {returnRecord && !isLoading && (
        <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1 text-xs">
          {/* Header */}
          <div className="flex items-start justify-between pb-3 border-b border-brand-border">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-600 shrink-0">
                <RotateCcw className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-bold text-brand-black">
                    #{returnRecord.return_number}
                  </h3>
                  <span
                    className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                      returnRecord.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {returnRecord.status.toUpperCase()}
                  </span>
                </div>
                <p className="text-xs text-brand-muted mt-0.5">
                  Original Invoice:{' '}
                  <strong className="text-brand-black">
                    #{returnRecord.sale?.invoice_number || 'N/A'}
                  </strong>{' '}
                  • Date: {formatDate(returnRecord.return_date)}
                </p>
              </div>
            </div>
          </div>

          {/* Refund & Customer Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-brand-bg p-3 rounded-lg border border-brand-border space-y-1">
              <p className="font-bold text-brand-black uppercase">Refund Summary</p>
              <div className="flex justify-between">
                <span className="text-brand-muted">Total Refund Issued:</span>
                <span className="font-bold text-brand-green text-sm">
                  {formatCurrency(returnRecord.refund_amount)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-brand-muted">Refund Method:</span>
                <span className="font-semibold text-brand-black uppercase">
                  {returnRecord.refund_method || 'Cash'}
                </span>
              </div>
            </div>

            <div className="bg-brand-bg p-3 rounded-lg border border-brand-border space-y-1">
              <p className="font-bold text-brand-black uppercase">Customer Details</p>
              {returnRecord.customer ? (
                <div>
                  <p className="font-semibold text-brand-black flex items-center space-x-1">
                    <User className="w-3.5 h-3.5 text-brand-green shrink-0" />
                    <span>{returnRecord.customer.name}</span>
                  </p>
                  {returnRecord.customer.phone && (
                    <p className="text-brand-muted">Phone: {returnRecord.customer.phone}</p>
                  )}
                </div>
              ) : (
                <p className="text-brand-muted italic">Walk-in Customer</p>
              )}
            </div>
          </div>

          {/* Reason */}
          {returnRecord.reason && (
            <div className="p-3 bg-amber-50 rounded border border-amber-200">
              <p className="font-bold text-amber-900">Reason for Return</p>
              <p className="text-amber-800 mt-0.5">{returnRecord.reason}</p>
            </div>
          )}

          {/* Returned Items Table */}
          <div>
            <h4 className="font-bold text-brand-black uppercase tracking-wider mb-2">
              Returned Items Breakdown
            </h4>
            <div className="bg-white border border-brand-border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-brand-bg border-b border-brand-border font-semibold text-brand-muted uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-3 py-2">Item Name / ID</th>
                    <th className="text-center px-3 py-2">Qty</th>
                    <th className="text-right px-3 py-2">Unit Refund</th>
                    <th className="text-right px-3 py-2">Total Refund</th>
                    <th className="text-center px-3 py-2">Restocked Status</th>
                    <th className="text-left px-3 py-2">Condition / Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-border">
                  {returnRecord.items?.map((item) => {
                    const lineRefund = item.quantity * item.unit_refund;
                    return (
                      <tr key={item.id}>
                        <td className="px-3 py-2 font-semibold text-brand-black">
                          {item.sale_item?.item_name || 'Sale Item'}
                        </td>
                        <td className="px-3 py-2 text-center font-bold">{item.quantity}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(item.unit_refund)}</td>
                        <td className="px-3 py-2 text-right font-bold text-brand-green">
                          {formatCurrency(lineRefund)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {item.restocked ? (
                            <span className="inline-flex items-center space-x-1 px-2 py-0.5 bg-green-100 text-green-800 rounded-full font-semibold text-[10px]">
                              <CheckCircle2 className="w-3 h-3 text-green-600" />
                              <span>Restocked</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center space-x-1 px-2 py-0.5 bg-red-100 text-red-800 rounded-full font-semibold text-[10px]">
                              <XCircle className="w-3 h-3 text-red-600" />
                              <span>Scrapped / Not Restocked</span>
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-brand-muted">
                          {item.condition_on_return && (
                            <span className="font-semibold text-brand-black mr-1">
                              [{item.condition_on_return}]
                            </span>
                          )}
                          {item.notes || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes */}
          {returnRecord.notes && (
            <div className="p-3 bg-brand-bg rounded border border-brand-border">
              <p className="font-bold text-brand-black">Internal Audit Notes</p>
              <p className="text-brand-muted">{returnRecord.notes}</p>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-end pt-3 border-t border-brand-border">
        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black">
          Close
        </button>
      </div>
    </>
  );
};
